/* ==========================================================================
   MEA CULPA — behaviour

   One easing curve, one set of durations, everything degrades to a working
   page without JS. Nothing animates on scroll more than once. No parallax.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* Sections re-render in the theme editor, so every binder runs more than
     once. claim() returns false for an element that is already wired up. */
  function claim(el, key) {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  }

  /* ------------------------------------------------------------------------
     Header — shrinks 89px -> 68px on scroll, one smooth transition
     ------------------------------------------------------------------------ */

  function initHeaderShrink() {
    if (!claim(document.body, 'headerBound')) return;

    var ticking = false;

    function apply() {
      document.body.classList.toggle('is-scrolled', window.scrollY > 21);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }, { passive: true });

    apply();
  }

  /* ------------------------------------------------------------------------
     Collections panel — full-width flat panel, no nested dropdowns
     ------------------------------------------------------------------------ */

  function initCollectionsPanel() {
    var trigger = $('[data-panel-trigger]');
    var panel = $('[data-panel]');
    if (!trigger || !panel) return;

    if (!claim(trigger, 'panelBound')) return;

    var header = trigger.closest('.header');

    function setOpen(open) {
      panel.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      setOpen(!panel.classList.contains('is-open'));
    });

    if (header && window.matchMedia('(hover: hover)').matches) {
      trigger.addEventListener('mouseenter', function () { setOpen(true); });
      header.addEventListener('mouseleave', function () { setOpen(false); });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setOpen(false);
    });
  }

  /* ------------------------------------------------------------------------
     Mobile menu
     ------------------------------------------------------------------------ */

  function initMobileMenu() {
    var burger = $('[data-menu-trigger]');
    var panel = $('[data-panel]');
    if (!burger || !panel) return;
    if (!claim(burger, 'menuBound')) return;

    burger.addEventListener('click', function (event) {
      event.preventDefault();
      var open = !panel.classList.contains('is-open');
      panel.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------------
     Marquee — duplicate the track so the -50% loop is seamless
     ------------------------------------------------------------------------ */

  function initMarquee() {
    $$('[data-marquee]').forEach(function (track) {
      if (track.dataset.marqueeReady === 'true') return;

      // The track holds one full set of messages; the animation translates by
      // -50%, so the content has to be doubled for the loop to be invisible.
      var set = track.innerHTML;
      var viewportWidth = track.parentElement.offsetWidth || window.innerWidth;

      // Repeat until one set is at least as wide as the viewport, then double.
      var guard = 0;
      while (track.scrollWidth < viewportWidth && guard < 10) {
        track.innerHTML += set;
        guard += 1;
      }
      track.innerHTML += track.innerHTML;
      track.dataset.marqueeReady = 'true';
    });
  }

  /* ------------------------------------------------------------------------
     Section entry — fade + rise 21px, 520ms, 40ms stagger, fires once
     ------------------------------------------------------------------------ */

  function initReveal() {
    var targets = $$('[data-reveal]').filter(function (el) {
      return !el.dataset.revealBound;
    });
    if (!targets.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.dataset.revealBound = 'true';
        el.classList.add('is-immediate');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        Array.prototype.forEach.call(entry.target.children, function (child, i) {
          child.style.setProperty('--reveal-delay', (i * 40) + 'ms');
        });
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    targets.forEach(function (el) {
      el.dataset.revealBound = 'true';
      // Anything already on screen at load is simply present — the entry
      // animation is for sections the visitor scrolls to.
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) {
        el.classList.add('is-immediate');
        return;
      }
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Collapsible rows — height + opacity, 320ms
     ------------------------------------------------------------------------ */

  function initAccordions() {
    $$('[data-accordion]').forEach(function (root) {
      var items = $$('[data-accordion-item]', root);

      items.forEach(function (item) {
        var trigger = $('[data-accordion-trigger]', item);
        var panel = $('[data-accordion-panel]', item);
        var icon = $('[data-accordion-icon]', item);
        if (!trigger || !panel) return;
        if (!claim(item, 'accordionBound')) return;

        function setOpen(open, animate) {
          panel.dataset.open = open ? 'true' : 'false';
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (icon) icon.textContent = open ? '−' : '+';

          if (!animate || REDUCED) {
            panel.style.height = open ? 'auto' : '0px';
            return;
          }

          if (open) {
            panel.style.height = panel.scrollHeight + 'px';
            panel.addEventListener('transitionend', function done(event) {
              if (event.propertyName !== 'height') return;
              panel.removeEventListener('transitionend', done);
              if (panel.dataset.open === 'true') panel.style.height = 'auto';
            });
          } else {
            // From 'auto' to a pixel value first, or the transition won't run.
            panel.style.height = panel.scrollHeight + 'px';
            window.requestAnimationFrame(function () {
              panel.style.height = '0px';
            });
          }
        }

        setOpen(item.dataset.open === 'true', false);

        trigger.addEventListener('click', function () {
          var willOpen = panel.dataset.open !== 'true';
          if (willOpen && root.dataset.accordionSingle === 'true') {
            items.forEach(function (other) {
              if (other === item) return;
              var otherTrigger = $('[data-accordion-trigger]', other);
              if (otherTrigger && otherTrigger.getAttribute('aria-expanded') === 'true') {
                otherTrigger.click();
              }
            });
          }
          setOpen(willOpen, true);
        });
      });
    });
  }

  /* ------------------------------------------------------------------------
     Product page — variant selection, gallery counter
     ------------------------------------------------------------------------ */

  function initVariantPicker() {
    var root = $('[data-variant-picker]');
    if (!root) return;
    if (!claim(root, 'pickerBound')) return;

    var variants;
    try {
      variants = JSON.parse($('[data-variant-json]', root).textContent);
    } catch (error) {
      return;
    }

    var idInput = $('[data-variant-id]', root);
    var priceEl = $('[data-variant-price]', root);
    var submit = $('[data-variant-submit]', root);
    var instalment = $('[data-variant-instalment]', root);
    var barPrice = $('[data-buybar-price]');
    var gallery = $('[data-gallery]');
    var payments = $('[data-payment-buttons]', root);
    var moneyFormat = root.dataset.moneyFormat || '${{amount}}';

    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      // Thousands separator, matching the storefront's default grouping.
      amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }

    function selectedOptions() {
      return $$('[data-option-index]', root).reduce(function (acc, group) {
        var chosen = $('[aria-checked="true"], :checked', group);
        var index = parseInt(group.dataset.optionIndex, 10);
        acc[index] = chosen ? (chosen.value || chosen.dataset.value) : null;
        return acc;
      }, []);
    }

    function findVariant(options) {
      return variants.find(function (variant) {
        return options.every(function (value, i) {
          return value === null || variant.options[i] === value;
        });
      });
    }

    // Show the chosen colourway's own photograph.
    //
    // The gallery renders a fixed number of images, but a product like the Fun
    // Day beanie has thirty-odd colourways — so the chosen one is usually not
    // in the grid at all. If it is, lead with it: scroll to it where the
    // gallery is a slider, otherwise pull it to the front with CSS order (the
    // DOM is untouched). If it is not, swap the lead frame's picture for it.
    function showVariantMedia(variant) {
      if (!gallery) return;

      var frames = $$('.frame', gallery);
      if (!frames.length) return;

      var index = variant.media_index;
      var inGrid = typeof index === 'number' && index >= 0 && index < frames.length;

      if (inGrid) {
        if (gallery.scrollWidth > gallery.clientWidth + 1) {
          gallery.scrollTo({
            left: frames[index].offsetLeft - gallery.offsetLeft,
            behavior: REDUCED ? 'auto' : 'smooth'
          });
          return;
        }
        frames.forEach(function (frame) { frame.style.order = ''; });
        if (index > 0) frames[index].style.order = '-1';
        return;
      }

      if (!variant.image) return;
      var img = $('img', frames[0]);
      if (!img) return;
      img.src = variant.image;
      img.removeAttribute('srcset');
      frames.forEach(function (frame) { frame.style.order = ''; });
      if (gallery.scrollWidth > gallery.clientWidth + 1) {
        gallery.scrollTo({ left: 0, behavior: REDUCED ? 'auto' : 'smooth' });
      }
    }

    function render() {
      var variant = findVariant(selectedOptions());

      // Sizes that don't exist in the chosen colour are struck through.
      var options = selectedOptions();
      $$('[data-option-index]', root).forEach(function (group) {
        var index = parseInt(group.dataset.optionIndex, 10);
        $$('[data-value]', group).forEach(function (control) {
          var probe = options.slice();
          probe[index] = control.dataset.value;
          var match = findVariant(probe);
          var available = !!(match && match.available);
          control.classList.toggle('is-unavailable', !available);
        });
      });

      if (!variant) {
        if (submit) {
          submit.textContent = root.dataset.labelUnavailable || 'Unavailable';
          submit.disabled = true;
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceEl.innerHTML =
            '<span class="price--was">' + formatMoney(variant.compare_at_price) + '</span>' +
            '<span class="price--sale">' + formatMoney(variant.price) + '</span>';
        } else {
          priceEl.innerHTML = '<span>' + formatMoney(variant.price) + '</span>';
        }
      }

      if (barPrice) {
        barPrice.textContent = formatMoney(variant.price);
        barPrice.classList.toggle('price--sale',
          !!(variant.compare_at_price && variant.compare_at_price > variant.price));
      }

      if (instalment) {
        instalment.textContent = (root.dataset.labelInstalment || 'or 4 interest-free payments of __AMOUNT__')
          .replace('__AMOUNT__', formatMoney(Math.round(variant.price / 4)));
      }

      if (submit) {
        submit.disabled = false;
        // Sold out becomes NOTIFY ME rather than a dead button.
        submit.textContent = variant.available
          ? (root.dataset.labelAdd || 'Add to cart')
          : (root.dataset.labelNotify || 'Notify me');
        submit.dataset.soldOut = variant.available ? 'false' : 'true';
      }

      // Buy it now posts whatever variant the form holds, so it goes away
      // rather than offering a checkout that cannot complete.
      if (payments) payments.hidden = !variant.available;

      showVariantMedia(variant);

      // Keep the URL shareable without adding a history entry per click.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    $$('[data-option-index]', root).forEach(function (group) {
      group.addEventListener('click', function (event) {
        var control = event.target.closest('[data-value]');
        if (!control || control.tagName === 'INPUT') return;
        $$('[data-value]', group).forEach(function (other) {
          other.classList.toggle('is-selected', other === control);
          other.setAttribute('aria-checked', other === control ? 'true' : 'false');
        });
        var label = $('[data-option-selected]', group.closest('[data-option-group]') || group);
        if (label && control.dataset.label) label.textContent = control.dataset.label;
        render();
      });
      group.addEventListener('change', render);
    });

    render();
  }

  /* Mobile gallery counter — "3 / 8" */
  function initGalleryCounter() {
    var gallery = $('[data-gallery]');
    var counter = $('[data-gallery-counter]');
    if (!gallery || !counter) return;

    var current = $('[data-gallery-current]', counter);
    var slides = $$('.frame', gallery);
    if (!current || !slides.length) return;
    if (!claim(gallery, 'galleryBound')) return;

    gallery.addEventListener('scroll', function () {
      var index = Math.round(gallery.scrollLeft / gallery.clientWidth) + 1;
      current.textContent = Math.min(Math.max(index, 1), slides.length);
    }, { passive: true });
  }

  /* ------------------------------------------------------------------------
     Collection — filter rail
     ------------------------------------------------------------------------ */

  function initFilters() {
    var form = $('[data-filter-form]');
    if (!form) return;
    if (!claim(form, 'filterBound')) return;

    // With JS the Apply button is redundant — a change filters straight away.
    var actions = $('.filter-actions', form);
    var apply = actions ? $('button[type="submit"]', actions) : null;
    if (apply) apply.hidden = true;

    form.addEventListener('change', function (event) {
      if (event.target.type === 'number') return;  // wait for the price commit
      form.submit();
    });

    $$('.filter-price__input', form).forEach(function (input) {
      input.addEventListener('change', function () { form.submit(); });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          form.submit();
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Collection — infinite scroll
     ------------------------------------------------------------------------ */

  function initInfiniteScroll() {
    var sentinel = $('[data-infinite-sentinel]');
    var grid = $('[data-infinite-grid]');
    if (!sentinel || !grid || !('IntersectionObserver' in window)) return;
    if (!claim(sentinel, 'infiniteBound')) return;

    var loading = false;

    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;

      var next = sentinel.dataset.nextUrl;
      if (!next || loading) return;

      loading = true;
      sentinel.textContent = sentinel.dataset.labelLoading || 'Loading more';

      fetch(next)
        .then(function (response) {
          if (!response.ok) throw new Error('Request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var incoming = doc.querySelector('[data-infinite-grid]');
          var incomingSentinel = doc.querySelector('[data-infinite-sentinel]');

          if (incoming) {
            while (incoming.firstElementChild) grid.appendChild(incoming.firstElementChild);
          }

          var nextUrl = incomingSentinel ? incomingSentinel.dataset.nextUrl : '';
          sentinel.dataset.nextUrl = nextUrl || '';
          sentinel.textContent = nextUrl
            ? (sentinel.dataset.labelLoading || 'Loading more')
            : (sentinel.dataset.labelEnd || 'End of collection');

          if (!nextUrl) observer.unobserve(sentinel);
          loading = false;
        })
        .catch(function () {
          // Leave the page usable and let the visitor page manually.
          sentinel.textContent = sentinel.dataset.labelError || 'Could not load more';
          observer.unobserve(sentinel);
          loading = false;
        });
    }, { rootMargin: '233px' });

    observer.observe(sentinel);
  }

  /* ------------------------------------------------------------------------
     Cart drawer — slides from the right, 380ms, ink overlay at 40%
     ------------------------------------------------------------------------ */

  var Cart = {
    drawer: null,
    overlay: null,
    lastFocus: null,

    init: function () {
      this.drawer = $('[data-cart-drawer]');
      this.overlay = $('[data-cart-overlay]');
      if (!this.drawer) return;

      var self = this;

      $$('[data-cart-open]').forEach(function (trigger) {
        if (trigger.dataset.cartBound === 'true') return;
        trigger.dataset.cartBound = 'true';
        trigger.addEventListener('click', function (event) {
          event.preventDefault();
          self.open();
        });
      });

      if (this.drawer.dataset.cartBound !== 'true') {
        this.drawer.dataset.cartBound = 'true';

        if (this.overlay) {
          this.overlay.addEventListener('click', function () { self.close(); });
        }

        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape' && self.isOpen()) self.close();
        });

        this.bindLines();
      }

      this.bindForms();
    },

    isOpen: function () {
      return this.drawer && this.drawer.classList.contains('is-open');
    },

    open: function () {
      if (!this.drawer) return;
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.classList.add('is-locked');
      var closer = $('[data-cart-close]', this.drawer);
      if (closer) closer.focus();
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    /* Add-to-cart without a page reload; falls back to a normal form POST. */
    bindForms: function () {
      var self = this;

      $$('[data-product-form]').forEach(function (form) {
        if (form.dataset.cartBound === 'true') return;
        form.dataset.cartBound = 'true';

        form.addEventListener('submit', function (event) {
          var submit = $('[data-variant-submit]', form);

          // Sold out: let the notify link behave like a link, not a cart add.
          if (submit && submit.dataset.soldOut === 'true') return;

          event.preventDefault();
          if (submit) submit.disabled = true;

          fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{
                id: $('[data-variant-id]', form).value,
                quantity: 1
              }]
            })
          })
            .then(function (response) {
              if (!response.ok) throw new Error('Add to cart failed');
              return response.json();
            })
            .then(function () { return self.refresh(); })
            .then(function () {
              self.open();
              if (submit) submit.disabled = false;
            })
            .catch(function () {
              // Network trouble — hand the visitor to the full cart page.
              form.submit();
            });
        });
      });
    },

    bindLines: function () {
      var self = this;
      if (!this.drawer) return;

      // Delegated: the drawer's contents are replaced on every cart change.
      this.drawer.addEventListener('click', function (event) {
        if (event.target.closest('[data-cart-close]')) {
          event.preventDefault();
          self.close();
          return;
        }

        var remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();

        fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: remove.dataset.cartRemove, quantity: 0 })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Update failed');
            return self.refresh();
          })
          .catch(function () { window.location.href = window.Shopify.routes.root + 'cart'; });
      });
    },

    /* Re-render the drawer and the header count from the cart drawer section. */
    refresh: function () {
      var self = this;
      return fetch(window.Shopify.routes.root + '?section_id=cart-drawer')
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var incoming = doc.querySelector('[data-cart-drawer]');
          if (incoming && self.drawer) {
            var wasOpen = self.isOpen();
            self.drawer.innerHTML = incoming.innerHTML;
            if (wasOpen) self.drawer.classList.add('is-open');
          }

          var count = doc.querySelector('[data-cart-count]');
          if (count) {
            $$('[data-cart-count]').forEach(function (el) {
              el.textContent = count.textContent;
            });
          }
        });
    }
  };

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function init() {
    document.documentElement.classList.remove('no-js');
    initHeaderShrink();
    initCollectionsPanel();
    initMobileMenu();
    initMarquee();
    initReveal();
    initAccordions();
    initVariantPicker();
    initGalleryCounter();
    initFilters();
    initInfiniteScroll();
    Cart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', function () {
    initMarquee();
    initReveal();
    initAccordions();
    initVariantPicker();
    initGalleryCounter();
    initFilters();
    initInfiniteScroll();
    initCollectionsPanel();
    initMobileMenu();
    Cart.init();
  });
})();
